import { savePendingEvent } from '../../../indexedDb/pendingEvents'
import { formatToISODate } from './formatTimestampToDate'

export const dateNow = () => new Date().toISOString()

const GA_TRACKING_ID = process.env.MIX_NEXT_PUBLIC_GA_TRACKING_ID

/**
 * Reads session from localStorage and returns user_id, company_id, and user_db_id for GA config.
 * Returns null for all when impersonating (so GA doesn't attribute to the impersonated user).
 */
export const getGAUserConfig = () => {
    if (typeof window === 'undefined') return {}
    const session = JSON.parse(window.localStorage.getItem('session') || '{}')
    const isImpersonating = session?.impersonated
    return {
        user_id: isImpersonating ? null : session?.user?.external_contact_id,
        company_id: isImpersonating
            ? null
            : session?.user?.company?.external_id,
        user_db_id: isImpersonating ? null : session?.user?.id,
    }
}

/**
 * Updates GA config with current user_id, company_id, and user_db_id from session.
 * Call after login, logout, starting/stopping impersonation, so GA has the correct identity.
 * `update: true` merges the identity without re-sending a page_view (no real navigation happened).
 */
export const updateGAUserConfig = () => {
    if (typeof window === 'undefined' || !window.gtag || !GA_TRACKING_ID) return
    if (process.env.MIX_APP_ENV !== 'production') return
    window.gtag('config', GA_TRACKING_ID, {
        ...getGAUserConfig(),
        update: true,
    })
}

const MAX_ANALYTICS_RETRIES = 3
const RETRY_DELAY_MS = 1000

export const sendAnalyticsEvent = (eventName, params, retryCount = 0) => {
    const env = process.env.MIX_APP_ENV
    const isProduction = env === 'production'

    if (!isProduction) {
        // Intentionally skip sending in non-production (no warning)
        return
    }

    if (typeof window === 'undefined') return

    if (window.gtag) {
        window.gtag('event', eventName, params)
        return
    }

    // GA script may still be loading; retry a few times
    if (retryCount < MAX_ANALYTICS_RETRIES) {
        console.warn(
            `Google Analytics not loaded yet. Retrying in ${RETRY_DELAY_MS /
                1000}s... (${retryCount + 1}/${MAX_ANALYTICS_RETRIES})`,
        )
        setTimeout(() => {
            sendAnalyticsEvent(eventName, params, retryCount + 1)
        }, RETRY_DELAY_MS)
    } else {
        console.warn(
            'Google Analytics did not load in time. Event not sent:',
            eventName,
        )
    }
}

export const trackRepairRequestCreated = (
    label,
    salesforceId,
    userId,
    dateOfCreation,
    dateOfSubmission,
) => {
    sendAnalyticsEvent('repair_request_created', {
        category: 'User Action',
        network_status: label,
        salesforce_id: salesforceId,
        user_salesforce_id: userId,
        date_of_creation: formatToISODate(dateOfCreation),
        date_of_submission: formatToISODate(dateOfSubmission),
    })
}

export const trackEstimateRequestCreated = (response, userId) => {
    const salesforceId = response?.data?.response?.salesforceId
    sendAnalyticsEvent('estimate_request_created', {
        category: 'User Action',
        network_status: 'Online',
        salesforce_id: salesforceId,
        user_salesforce_id: userId,
        date_of_creation: formatToISODate(dateNow()),
        date_of_submission: formatToISODate(dateNow()),
    })
}

export const trackPartsRequestCreated = (
    label,
    salesforceId,
    userId,
    dateOfCreation,
    dateOfSubmission,
) => {
    sendAnalyticsEvent('parts_request_created', {
        category: 'User Action',
        network_status: label,
        salesforce_id: salesforceId,
        user_salesforce_id: userId,
        date_of_creation: formatToISODate(dateOfCreation),
        date_of_submission: formatToISODate(dateOfSubmission),
    })
}

export const trackRepairRequestCreatedOnline = (response, userId) => {
    const salesforceId = response?.data?.response?.salesforceId
    trackRepairRequestCreated(
        'Online',
        salesforceId,
        userId,
        formatToISODate(dateNow()),
        formatToISODate(dateNow()),
    )
}

// Time Between Offline Request Creation and Submission
export const trackRepairRequestCreatedOffline = (response, userId, request) => {
    const salesforceId = response?.data?.response?.salesforceId
    const timeOffline = Date.now() - new Date(request?.id).getTime()

    trackRepairRequestCreated(
        'Offline',
        salesforceId,
        userId,
        formatToISODate(request?.id),
        formatToISODate(dateNow()),
    )

    sendAnalyticsEvent('offline_request_sync_time', {
        category: 'Offline Usage',
        user_salesforce_id: userId,
        time_offline: timeOffline,
    })
}

export const trackPartsRequestCreatedOnline = (response, userId) => {
    const salesforceId = response?.data?.response?.salesforceId
    trackPartsRequestCreated(
        'Online',
        salesforceId,
        userId,
        formatToISODate(dateNow()),
        formatToISODate(dateNow()),
    )
}

export const trackPartsRequestCreatedOffline = (response, userId, request) => {
    const salesforceId = response?.data?.response?.salesforceId
    trackPartsRequestCreated(
        'Offline',
        salesforceId,
        userId,
        formatToISODate(request?.id),
        formatToISODate(dateNow()),
    )
}

// Track Number of Offline Requests Stored & Sent Later
export const trackPendingOfflineRequests = async (
    userId,
    pendingRequestsLength,
) => {
    sendAnalyticsEvent('offline_requests_pending', {
        category: 'Offline Pending Requests',
        network_status: 'Offline',
        user_salesforce_id: userId,
        count_pending_requests: pendingRequestsLength,
    })
}

//revisar esto
export const trackNetworkChangeDuringRequest = ({
    userId,
    initialStatus,
    finalStatus,
}) => {
    sendAnalyticsEvent('network_change_during_request', {
        category: 'Network',
        user_salesforce_id: userId,
        initial_status: initialStatus,
        final_status: finalStatus,
    })
}

export const getDiscardCount = () => {
    return parseInt(localStorage.getItem('discarded_requests_count') || '0', 10)
}

export const incrementDiscardCount = () => {
    const currentCount = getDiscardCount()
    localStorage.setItem('discarded_requests_count', currentCount + 1)
}

export const sendDiscardMetric = () => {
    const discardCount = getDiscardCount()
    if (discardCount > 0) {
        sendAnalyticsEvent('discarded_requests_count', { count: discardCount })
        localStorage.setItem('discarded_requests_count', '0')
    }
}

export const trackDiscardEvent = (request, userId, isOffline) => {
    const eventData = {
        event: 'discard_pending_request',
        category: 'User Action',
        request_id: request?.id,
        status: request?.status,
        user_salesforce_id: userId,
        timestamp: new Date().toISOString(),
        network_status: isOffline ? 'Offline' : 'Online',
    }

    incrementDiscardCount()
    if (!isOffline) {
        sendAnalyticsEvent(eventData.event, eventData)
    } else {
        savePendingEvent(eventData)
    }
}

/**
 * Track when a user interacts with the map (click or drag).
 */
export const trackMapInteraction = userId => {
    sendAnalyticsEvent('map_interaction', {
        user_salesforce_id: userId,
        server: process.env.MIX_APP_ENV,
    })
}
